import { useCallback, useMemo, useState } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../AppContext";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { UserRole } from "../../../../common/enums";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";
import {
  IInventoryItemInputModel,
  IInventoryCategoryFormModel,
  IInventoryCategoryModel,
  inventoryCategorySchema,
  getInventoryCategoryDefaults,
} from "../models/inventoryNomenclatureModels";
import { ICustomCategoryFormConfig } from "../../../../common/nomenclatures/components/CategoryFormDialog";
import { getInventoryItemDisplayName } from "../../../../common/utilities/inventoryDisplayUtility";

interface IAdjustDialogState {
  itemId: number;
  itemName: string;
  currentQuantity: number;
}

/**
 * Encapsulates all page-level logic for the inventory products admin:
 *   - fetching categories (for item-form dropdown + emoji lookup on rows)
 *   - fetching studio settings (timezone for history dialogs)
 *   - role gating for the "adjust quantity" action
 *   - adjust-quantity dialog open/close state
 *   - the custom category form config (emoji field)
 *   - the submit payload transformer (strip server-managed fields).
 */
export const useInventoryNomenclaturePage = () => {
  const { selectedStudioId, currentUser } = useAppContext();
  const endpoints = endpointsMetadata.inventoryItemNomenclature;

  const { data: categories = [] } = useApiQuery<IInventoryCategoryModel[]>({
    queryKey: [endpoints.getAllCategories, selectedStudioId],
    endpoint: endpoints.getAllCategories,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const { data: studioSettings } = useApiQuery<IStudioSettingsModel>({
    queryKey: [endpointsMetadata.studio.getStudioSettings, selectedStudioId],
    endpoint: endpointsMetadata.studio.getStudioSettings,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const [adjustDialog, setAdjustDialog] = useState<IAdjustDialogState | null>(null);

  // Roles numerically: Employee_Manager=15 < Employee=20 < Tenant=30. All "manager or above"
  // are allowed to manually adjust stock. Regular users and instructors are not.
  const role = getCurrentUserRole(currentUser);
  const canAdjust =
    role !== undefined &&
    role !== UserRole.User &&
    role !== UserRole.Employee_Instructor &&
    role !== UserRole.Employee_Instructor_Group_Events;

  const activeCategories = useMemo(
    () => categories.filter((c) => !c.isDeleted),
    [categories]
  );

  const customCategoryConfig: ICustomCategoryFormConfig<IInventoryCategoryFormModel> = useMemo(
    () => ({
      formFields: null, // supplied in the page component to keep JSX in .tsx only.
      schema: inventoryCategorySchema,
      defaultValues: getInventoryCategoryDefaults,
      mapToFormValues: (category: IInventoryCategoryModel) => ({
        id: category.id,
        name: category.name,
        emoji: category.emoji ?? "",
      }),
    }),
    []
  );

  const openAdjustDialog = useCallback((item: IInventoryItemInputModel) => {
    if (!item.id) return;
    setAdjustDialog({
      itemId: item.id,
      itemName: getInventoryItemDisplayName(item.name ?? "", item.brand),
      currentQuantity: Number(item.currentQuantity ?? 0),
    });
  }, []);

  const closeAdjustDialog = useCallback(() => setAdjustDialog(null), []);

  /**
   * Strip frontend-only / server-managed fields from the form payload before
   * submitting. `currentQuantity` is only mutated via transfers, sales, or the
   * adjust-quantity endpoint — never via the main Create/Update endpoints.
   */
  const transformPayload = useCallback(
    (data: IInventoryItemInputModel): Partial<IInventoryItemInputModel> => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { currentQuantity, isDeleted, sortOrder, ...apiPayload } = data;
      return apiPayload;
    },
    []
  );

  return {
    endpoints,
    categories,
    activeCategories,
    studioSettings,
    canAdjust,
    customCategoryConfig,
    adjustDialog,
    openAdjustDialog,
    closeAdjustDialog,
    transformPayload,
  };
};
